import { useCallback, useEffect, useMemo, useState } from "react";
import { Feature, FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import { useColorModeValue, theme } from "@chakra-ui/react";
import { Layer, Source, useMap } from "react-map-gl";
import Tilebelt from "tilebelt";
import { bbox } from "@turf/bbox";
import rbush from "@turf/geojson-rbush";

type Props = {
  data?: FeatureCollection,
  beforeId?: string,
}

function MapGrid({ data, beforeId }: Props) {
  const {current: map} = useMap();
  const [grid, setGrid] = useState<FeatureCollection>();
  const [footprintsTree, setFootprintsTree] = useState<ReturnType<typeof rbush>>();
  const gridLineColor = useColorModeValue(theme.colors.blackAlpha["50"], theme.colors.whiteAlpha["50"]);
  // Creates a GeoJSON collection with the grid for the current zoom level
  const makeGrid = useCallback(() => {
    if (!map) return;

    const bounds = map.getBounds().toArray().flat();
    let zoom = map.getZoom();

    if (!zoom || !bounds || zoom > 8) return;

    zoom += 4; // We want a grid smaller than the tiles on the current zoom level.

    const ll = Tilebelt.pointToTile(bounds[0], bounds[1], zoom);
    const ur = Tilebelt.pointToTile(bounds[2], bounds[3], zoom);

    const boxes = [];
    for (let x = ll[0]; x <= ur[0]; x++) {
      for (let y = ll[1]; y >= ur[1]; y--) {
        const tile = [x, y, zoom];
        const feature: Feature = {
          type: "Feature",
          properties: {
            _quadKey: Tilebelt.tileToQuadkey(tile),
            id: boxes.length,
            tile: tile.join("/")
          },
          geometry: Tilebelt.tileToGeoJSON(tile)
        };
        boxes.push(feature);
      }
    }

    setGrid({
      type: "FeatureCollection",
      features: boxes
    });
  }, [map]);

  // Register event handlers to update the grid when the map extent changes
  useEffect(() => {
    makeGrid();
    map?.on("dragend", makeGrid);
    map?.on("zoomend", makeGrid);

    return () => {
      map?.off("dragend", makeGrid);
      map?.off("zoomend", makeGrid);
    };
  }, [makeGrid, map]);

  // Build the RTree for the data, when the data changes
  useEffect(() => {
    if (!data) return;
    const tree = rbush();
    tree.load(data);
    setFootprintsTree(tree);
  }, [data]);

  // Make the grid data. Take the current grid, and get the number of scene intersections with each grid cell
  const gridData = useMemo((): FeatureCollection<Geometry, GeoJsonProperties> | undefined => {
    if (!grid || !footprintsTree) return;

    const features = grid.features.map((f) => {
      const matches = footprintsTree.search(bbox(f)).features.length;

      return {
        ...f,
        properties: {
          ...f.properties,
          matches
        }
      };
    });

    return {
      type: "FeatureCollection",
      features
    };
  }, [grid, footprintsTree]);

  if (gridData) {
    return (
      <Source
        id="grid"
        type="geojson"
        data={gridData}
      >
        <Layer
          id="grid-fill"
          beforeId={beforeId}
          type="fill"
          paint={{
            "fill-color": {
              "property": "matches",
              "stops": [
                [1, "rgba(2, 194, 235, 0)"],
                [2, "rgba(2, 194, 235, .1)"],
                [3, "rgba(2, 194, 235, .2)"],
                [4, "rgba(2, 194, 235, .3)"],
                [5, "rgba(2, 194, 235, .4)"],
                [6, "rgba(2, 194, 235, .5)"],
                [7, "rgba(2, 194, 235, .6)"],
                [8, "rgba(2, 194, 235, .7)"],
                [9, "rgba(2, 194, 235, .8)"],
                [10, "rgba(2, 194, 235, .9)"]
              ]
            }}}
        />
        <Layer
          id="grid-outline"
          type="line"
          paint={{"line-color": gridLineColor,"line-width": 1}}
        />
      </Source>
    );
  }

  return null;
}

export default MapGrid;
